import { useNostr } from './useNostr';
import { useCurrentUser } from './useCurrentUser';
import { useQuery, useQueryClient } from '@tanstack/react-query';

/**
 * NIP-85 Trust/Mute list subscription system
 * Allows users to subscribe to trust sets (curated follow lists from trusted sources)
 * and mute lists (curated block lists from moderators) via kind 10000 and kind 30000.
 */

export interface Nip85List {
  pubkey: string;
  name?: string;
  pTags: string[];
  tTags: string[];
}

/**
 * Fetch a standard list (kind 10000) or a set (kind 30000 with d tag) for a pubkey
 * Returns all p-tagged pubkeys (muted/trusted accounts)
 */
export function useNip85List(pubkey: string | undefined, kind: 10000 | 30000, dTag?: string) {
  const { nostr } = useNostr();

  return useQuery({
    queryKey: ['nip85-list', pubkey, kind, dTag],
    queryFn: async (): Promise<Nip85List | null> => {
      if (!nostr || !pubkey) return null;

      const filter: any = { kinds: [kind], authors: [pubkey], limit: 1 };
      if (dTag) {
        filter['#d'] = [dTag];
      }

      const events = await nostr.query([filter]);
      if (!events[0]) return null;

      const event = events[0];

      return {
        pubkey,
        name: event.tags.find(([n]) => n === 'title')?.[1] || event.tags.find(([n]) => n === 'd')?.[1],
        pTags: event.tags.filter(([n]) => n === 'p').map(([_, pk]) => pk),
        tTags: event.tags.filter(([n]) => n === 't').map(([_, tag]) => tag),
      };
    },
    enabled: !!pubkey,
    staleTime: 10 * 60 * 1000,
  });
}

export interface TrustSource {
  pubkey: string;
  label: string;
  kind: 'trust' | 'mute';
}

const TRUST_SOURCES_KEY = 'trust-sources';

/**
 * Get all subscribed trust/mute sources from localStorage
 */
export function getTrustSources(): TrustSource[] {
  try {
    return JSON.parse(localStorage.getItem(TRUST_SOURCES_KEY) || '[]');
  } catch {
    return [];
  }
}

/**
 * Add a trust/mute source subscription (persisted to localStorage)
 */
export function addTrustSource(source: TrustSource): void {
  const sources = getTrustSources().filter(s => s.pubkey !== source.pubkey);
  sources.push(source);
  localStorage.setItem(TRUST_SOURCES_KEY, JSON.stringify(sources));
}

/**
 * Remove a trust/mute source subscription
 */
export function removeTrustSource(pubkey: string): void {
  const sources = getTrustSources().filter(s => s.pubkey !== pubkey);
  localStorage.setItem(TRUST_SOURCES_KEY, JSON.stringify(sources));
}

/**
 * Hook that fetches all subscribed trust/mute lists and merges them
 * Returns accumulated trusted pubkeys and muted pubkeys
 */
export function useTrustSubscriptions() {
  const { nostr } = useNostr();
  const { user } = useCurrentUser();
  const sources = getTrustSources();

  // Fetch each source's list
  const queries = sources.map(source =>
    useQuery({
      queryKey: ['nip85-list', source.pubkey, 10000],
      queryFn: async (): Promise<Nip85List | null> => {
        if (!nostr || !source.pubkey) return null;
        const events = await nostr.query([{
          kinds: [10000],
          authors: [source.pubkey],
          limit: 10,
        }]);
        // Find the one with appropriate d tag or any mute/trust list
        const event = events[0];
        if (!event) return null;

        return {
          pubkey: source.pubkey,
          name: event.tags.find(([n]) => n === 'd')?.[1],
          pTags: event.tags.filter(([n]) => n === 'p').map(([_, pk]) => pk),
          tTags: event.tags.filter(([n]) => n === 't').map(([_, tag]) => tag),
        };
      },
      enabled: !!source.pubkey,
      staleTime: 10 * 60 * 1000,
    })
  );

  // Merge all trusted pubkeys from trust sources
  const trustedPubkeys = new Set<string>();
  // Merge all muted pubkeys from mute sources
  const mutedPubkeys = new Set<string>();

  for (let i = 0; i < sources.length; i++) {
    const data = queries[i]?.data;
    if (!data) continue;

    for (const pk of data.pTags) {
      if (sources[i].kind === 'trust') {
        trustedPubkeys.add(pk);
      } else if (sources[i].kind === 'mute') {
        mutedPubkeys.add(pk);
      }
    }
  }

  return {
    trustedPubkeys,
    mutedPubkeys,
    isLoading: queries.some(q => q.isLoading),
    sources,
  };
}